// ✅ src/pages/Asset/ModelMasterListing.jsx
// FULL FILE - API + SEARCH + FILTER + PAGINATION + DELETE + LOCALSTORAGE HEADERS

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaEdit, FaTrash, FaSearch } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/modelListing";

const ModelMasterListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ UI state
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // ✅ server paging
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // ✅ filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(""); // "" = all, "Active", "Inactive"

  // ---------------------- API ----------------------

  const fetchList = async (overridePage = page) => {
    try {
      setLoading(true);

      const params = {
        page: overridePage,
        pageSize,
      };

      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;

      const { data } = await axios.get(`${API_BASE}/list`, {
        params,
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load models");
        setRows([]);
        setTotal(0);
        setTotalPages(1);
        return;
      }

      setRows(data.data || []);

      // ✅ supports both old and new backend response formats
      setPage(data.paging?.page || data.page || overridePage || 1);
      setTotalPages(data.paging?.totalPages || data.totalPages || 1);
      setTotal(data.paging?.total || data.totalCount || 0);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "API error");
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, commonHeaders]);

  // ✅ search debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchList(1);
    }, 400);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, commonHeaders]);

  // ---------------------- Actions ----------------------

  const handleAddNew = () => {
    navigate("/Asset/ModelMaster");
  };

  const handleEdit = (row) => {
    navigate(`/Asset/ModelMaster?id=${row.id}`);
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`Delete model "${row.modelName}" ?`);
    if (!ok) return;

    try {
      const { data } = await axios.delete(`${API_BASE}/delete/${row.id}`, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Delete failed");
        return;
      }

      toast.success(data?.message || "Deleted");

      if (rows.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        fetchList(page);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Delete error");
    }
  };

  const handlePageChange = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  const renderPageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 8;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const left = Math.max(2, page - 2);
    const right = Math.min(totalPages - 1, page + 2);

    pages.push(1);
    if (left > 2) pages.push("...");

    for (let i = left; i <= right; i++) pages.push(i);

    if (right < totalPages - 1) pages.push("...");
    pages.push(totalPages);

    return pages;
  }, [page, totalPages]);

  // ---------------------- UI ----------------------

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MODEL MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Model Master Listing
              </h1>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-200" : "text-gray-600"}`}>
                Total: <span className="font-semibold">{total}</span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  isDark ? "border-white/20 bg-white/10" : "border-gray-300 bg-white"
                }`}
              >
                <FaSearch className={`${isDark ? "text-gray-200" : "text-gray-500"}`} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search model name / code / status..."
                  className={`outline-none bg-transparent text-sm w-64 ${
                    isDark
                      ? "text-white placeholder:text-gray-300"
                      : "text-gray-900 placeholder:text-gray-400"
                  }`}
                />
              </div>

              <select
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value);
                }}
                className={`px-3 py-2 rounded-lg border text-sm ${
                  isDark
                    ? "border-white/20 bg-white/10 text-white"
                    : "border-gray-300 bg-white text-gray-900"
                }`}
              >
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>

              <button
                onClick={handleAddNew}
                className={`flex items-center justify-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Sl No
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Model Name
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Model Code
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Status
                    </th>
                    <th className="px-6 py-4 text-center font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center">
                        <span className={`${isDark ? "text-gray-200" : "text-gray-600"}`}>
                          Loading...
                        </span>
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center">
                        <span className={`${isDark ? "text-gray-200" : "text-gray-600"}`}>
                          No records found.
                        </span>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr
                        key={row.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * pageSize + index + 1}
                        </td>

                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 font-medium ${
                            isDark ? "text-blue-300" : "text-blue-600"
                          }`}
                        >
                          {row.modelName}
                        </td>

                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {row.modelCode}
                        </td>

                        <td className="px-6 py-4 text-center border-r border-gray-200 dark:border-gray-700">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              String(row.status).toLowerCase() === "active"
                                ? isDark
                                  ? "bg-green-800 text-green-100"
                                  : "bg-green-100 text-green-800"
                                : isDark
                                ? "bg-red-800 text-red-100"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(row)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                                isDark
                                  ? "border-white/20 bg-white/10 hover:bg-white/20 text-white"
                                  : "border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
                              }`}
                            >
                              <FaEdit />
                              Edit
                            </button>

                            <button
                              onClick={() => handleDelete(row)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                                isDark
                                  ? "border-red-400/40 bg-red-500/20 hover:bg-red-500/30 text-white"
                                  : "border-red-300 bg-red-50 hover:bg-red-100 text-red-700"
                              }`}
                            >
                              <FaTrash />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-start mt-6">
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  page <= 1
                    ? isDark
                      ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              {renderPageNumbers.map((p, idx) => (
                <button
                  key={idx}
                  onClick={() => p !== "..." && handlePageChange(p)}
                  disabled={p === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    p === page
                      ? "bg-blue-600 text-white border-blue-600"
                      : p === "..."
                      ? isDark
                        ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  page >= totalPages
                    ? isDark
                      ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default ModelMasterListing;